import React, { Component } from "react";
import SaveHOC from "../HOC/SaveHOC";
import Row from "./Row";
import { connect } from "react-redux";
import IDocuments, {
  EDocumentPrivacy,
  EDocumentType,
  IDocumentData,
  TDocuments,
} from "./IDocuments";
import { setFormDirty } from "@company/features/action.company";
import IdentityUploader from "../IdentityUploader/IdentityUploader";
import { TReducers } from "@features/reducers";
import { groupDocumentsByYear, sortByDate } from "./helpers";
import NoDocuments from "../../icons/nodocuments.svg";

const isEqual = require("lodash.isequal");
import * as styles from "./DocumentsStyles.module.scss";
import * as appStyles from "../../AppStyle.module.scss";

class Documents extends Component<IDocuments, TDocuments> {
  state: TDocuments = {
    showedAllUserDocs: false,
    showedAllInvestorDocs: false,
    data: this.props.documents,
    userDocs: Documents.selectUserDocs(this.props.documents),
    investorDocs: Documents.selectInvestorDocs(this.props.documents),
    whitepaper: Documents.selectWhitepaperDoc(this.props.documents),
    uploadUserDocument: false,
    uploadWhitepaper: false,
    uploadInvestorDocument: false,
    prevDocuments: [],
  };

  constructor(props: IDocuments) {
    super(props);
  }

  static getDerivedStateFromProps(
    nextProps: IDocuments,
    prevState: TDocuments
  ) {
    // update only when parent props changed
    if (!isEqual(nextProps.documents, prevState.prevDocuments)) {
      return {
        data: nextProps.documents,
        userDocs: Documents.selectUserDocs(nextProps.documents),
        investorDocs: Documents.selectInvestorDocs(nextProps.documents),
        whitepaper: Documents.selectWhitepaperDoc(nextProps.documents),
        prevDocuments: nextProps.documents,
      };
    }

    return null;
  }

  static selectUserDocs = (documents: IDocumentData[]) => {
    return documents.filter((doc: IDocumentData) => {
      return (
        Documents.checkDocumentType(doc.type, doc.privacy) === "user" &&
        !doc._destroy
      );
    });
  };

  static selectInvestorDocs = (documents: IDocumentData[]) => {
    return documents.filter((doc: IDocumentData) => {
      return (
        Documents.checkDocumentType(doc.type, doc.privacy) === "investor" &&
        !doc._destroy
      );
    });
  };

  static selectWhitepaperDoc = (documents: IDocumentData[]) => {
    if (documents.length > 0) {
      const whitepaper = documents.find((doc: IDocumentData) => {
        return (
          Documents.checkDocumentType(doc.type, doc.privacy) === "whitepaper" &&
          !doc._destroy
        );
      });

      return whitepaper === undefined ? {} : whitepaper;
    }

    return {};
  };

  static checkDocumentType = (
    type: EDocumentType,
    privacy: EDocumentPrivacy
  ) => {
    if (type === EDocumentType.other && privacy === EDocumentPrivacy.user) {
      return "user";
    }

    if (type === EDocumentType.other && privacy === EDocumentPrivacy.investor) {
      return "investor";
    }

    if (
      type === EDocumentType.whitepaper &&
      privacy === EDocumentPrivacy.public
    ) {
      return "whitepaper";
    }

    return null;
  };

  componentDidUpdate(prevProps: IDocuments, prevState: TDocuments) {
    if (
      this.props.editMode &&
      !this.props.formDirty &&
      !isEqual(prevState.data, this.state.data)
    ) {
      this.props.dispatch(setFormDirty(true));
    }
  }

  shouldComponentUpdate(nextProps: IDocuments, nextState: TDocuments) {
    if (this.props.formDirty !== nextProps.formDirty) return false;

    return true;
  }

  // show user or investor documents
  showDocuments(type: "user" | "investor") {
    const howManyShow = 5;
    let array = this.state.userDocs;
    let title = "Documents";
    let showedAllDocs = this.state.showedAllUserDocs;
    let functionClick = this.showAllUserDocs;

    if (type === "investor") {
      array = sortByDate(this.state.investorDocs);
      title = "Investor only documents";
      showedAllDocs = this.state.showedAllInvestorDocs;
      functionClick = this.showAllInvestorDocs;
    }

    if (array.length > 0) {
      const docs = showedAllDocs ? array : array.slice(0, howManyShow);
      const docsGroupedByYear = groupDocumentsByYear(docs);

      const years = Object.keys(docsGroupedByYear);

      return (
        <div className={styles.documents}>
          <h3>{title}</h3>
          {years
            .sort()
            .reverse()
            ?.map((year) => {
              return (
                <div key={year} className={styles.documentsSection}>
                  {year !== "NaN" && (
                    <p className={styles.documentsYear}>Year: {year}</p>
                  )}
                  {docsGroupedByYear[year]?.map((doc) => {
                    return (
                      <Row
                        document={doc}
                        editMode={this.props.editMode}
                        delete={this.deleteDocument}
                        key={doc.id}
                      />
                    );
                  })}
                </div>
              );
            })}
          {!showedAllDocs && array.length > howManyShow ? (
            <button
              onClick={functionClick}
              className={styles.documents_showAll}
            >
              {`Show All (${array.length})`}
            </button>
          ) : null}
        </div>
      );
    }

    return (
      <div className={styles.documents}>
        <h3>{title}</h3>
        <div className={styles.empty}>
          <NoDocuments/>
          <p>No Documents</p>
        </div>
      </div>
    );
  }

  // show user, investor and whitepaper documents in edit mode
  showEditDocuments = () => {
    return (
      <React.Fragment>
        <div className={[styles.documents, styles.documentsEdit].join(" ")}>
          <h3>Documents</h3>
          <button
            className={appStyles.addButton}
            onClick={this.setUploadDocumentFlag}
            data-type="user"
          >
            {this.state.uploadUserDocument ? "Cancel upload" : "Add Documents"}
          </button>
          {this.state.uploadUserDocument ? (
            <IdentityUploader
              description="Click here or drop file to upload."
              fileSizeMb={20}
              type="other"
              privacy={EDocumentPrivacy.user}
              filename="Document"
              getUploadResponse={(e: Event) => {
                this.getUploadResponse(
                  e,
                  EDocumentType.other,
                  EDocumentPrivacy.user
                );
              }}
            />
          ) : this.state.userDocs.length > 0 ? (
            this.state.userDocs.map((doc: IDocumentData) => (
              <Row
                document={doc}
                editMode={this.props.editMode}
                delete={this.deleteDocument}
                key={doc.id}
                dispatch={this.props.dispatch}
              />
            ))
          ) : (
            <div className={styles.empty}>
              <p>There's no Documents added yet</p>
            </div>
          )}
        </div>
        {this.props.isOwner ? (
          <div className={[styles.documents, styles.documentsEdit].join(" ")}>
            <h3>Investor only documents</h3>
            <button
              className={appStyles.addButton}
              onClick={this.setUploadDocumentFlag}
              data-type="investor"
            >
              {this.state.uploadInvestorDocument
                ? "Cancel upload"
                : "Add Documents"}
            </button>
            {this.state.uploadInvestorDocument ? (
              <IdentityUploader
                description="Click here or drop file to upload."
                fileSizeMb={20}
                type="other"
                privacy={EDocumentPrivacy.investor}
                filename="Document"
                getUploadResponse={(e: Event) => {
                  this.getUploadResponse(
                    e,
                    EDocumentType.other,
                    EDocumentPrivacy.investor
                  );
                }}
              />
            ) : this.state.investorDocs.length > 0 ? (
              this.state.investorDocs.map((doc: IDocumentData) => (
                <Row
                  document={doc}
                  editMode={this.props.editMode}
                  delete={this.deleteDocument}
                  key={doc.id}
                  dispatch={this.props.dispatch}
                />
              ))
            ) : (
              <div className={styles.empty}>
                <p>There's no Documents added yet</p>
              </div>
            )}
          </div>
        ) : null}
        <div className={[styles.documents, styles.documentsEdit].join(" ")}>
          <h3>Whitepaper</h3>
          {Object.keys(this.state.whitepaper).length > 0 ? null : (
            <button
              className={appStyles.addButton}
              onClick={this.setUploadDocumentFlag}
              data-type="whitepaper"
            >
              {this.state.uploadWhitepaper ? "Cancel upload" : "Add Whitepaper"}
            </button>
          )}
          {this.state.uploadWhitepaper ? (
            <IdentityUploader
              description="Click here or drop whitepaper to upload."
              fileSizeMb={20}
              type="whitepaper"
              privacy={EDocumentPrivacy.public}
              filename="Whitepaper"
              getUploadResponse={(e: Event) => {
                this.getUploadResponse(
                  e,
                  EDocumentType.whitepaper,
                  EDocumentPrivacy.public
                );
              }}
            />
          ) : Object.keys(this.state.whitepaper).length > 0 ? (
            <Row
              document={this.state.whitepaper}
              editMode={this.props.editMode}
              delete={this.deleteDocument}
              dispatch={this.props.dispatch}
            />
          ) : (
            <div className={styles.empty}>
              <p>There's no Whitepaper added yet</p>
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  getUploadResponse = (
    resp: any,
    type: EDocumentType,
    privacy: EDocumentPrivacy
  ) => {
    const document = {
      file: resp.file,
      file_name: resp.file_name,
      id: resp.id,
      type: type,
      privacy: privacy,
    };
    const documentType = Documents.checkDocumentType(type, privacy);

    if (documentType === "whitepaper") {
      this.setState({
        uploadWhitepaper: false,
        whitepaper: document,
        data: [...this.state.data, document],
      });
    } else if (documentType === "user") {
      this.setState({
        uploadUserDocument: false,
        userDocs: [...this.state.userDocs, document],
        data: [...this.state.data, document],
      });
    } else if (documentType === "investor") {
      this.setState({
        uploadInvestorDocument: false,
        investorDocs: [...this.state.investorDocs, document],
        data: [...this.state.data, document],
      });
    }
  };

  deleteDocument = (id: number) => {
    if (confirm("Are you sure?")) {
      let documentType;
      const _data: IDocumentData[] = [];

      this.state.data.forEach((elem) => {
        if (elem.id === id) {
          documentType = Documents.checkDocumentType(elem.type, elem.privacy);
          _data.push({ ...elem, _destroy: 1 });
        } else _data.push(elem);
      });

      if (documentType === "user") {
        this.setState({
          data: _data,
          userDocs: Documents.selectUserDocs(_data),
        });
      } else if (documentType === "investor") {
        this.setState({
          data: _data,
          investorDocs: Documents.selectInvestorDocs(_data),
        });
      } else if (documentType === "whitepaper") {
        this.setState({
          data: _data,
          whitepaper: Documents.selectWhitepaperDoc(_data),
        });
      }
    }
  };

  setUploadDocumentFlag = (e: React.BaseSyntheticEvent) => {
    const type = e.target.getAttribute("data-type");
    if (type === "user") {
      this.setState({ uploadUserDocument: !this.state.uploadUserDocument });
    } else if (type === "investor") {
      this.setState({
        uploadInvestorDocument: !this.state.uploadInvestorDocument,
      });
    } else if (type === "whitepaper") {
      this.setState({ uploadWhitepaper: !this.state.uploadWhitepaper });
    }
  };

  showAllUserDocs = () => {
    this.setState({ showedAllUserDocs: true });
  };

  showAllInvestorDocs = () => {
    this.setState({ showedAllInvestorDocs: true });
  };

  render() {
    return this.props.editMode ? (
      this.showEditDocuments()
    ) : (
      <React.Fragment>
        {this.showDocuments("user")}
        {this.props.isInvestorZone &&
          (this.props.hasInvestorAccess || this.props.isOwner) &&
          this.showDocuments("investor")}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (store: TReducers) => {
  return {
    documents: store.companyReducer.documents,
    formDirty: store.companyReducer.formDirty,
  };
};

export default connect(mapStateToProps)(SaveHOC(Documents, "documents"));
